import React from 'react';
import { InlineSpinner } from '../../common/loading';
import { Box, Button, alpha, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SEARCE_COLORS } from '../../../theme';
import { RequestType } from './types';

interface ActionButtonsProps {
  selectedRequestType: RequestType;
  isFormDisabled: boolean;
  loading: boolean;
  submissionSuccess: boolean;
  onBack: () => void;
  /** Solution story: save draft without slides */
  onSaveDraft?: () => void;
  draftSaving?: boolean;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  selectedRequestType,
  isFormDisabled,
  loading,
  submissionSuccess,
  onBack,
  onSaveDraft,
  draftSaving = false,
}) => {
  const isFeedbackOrSolutionStory = selectedRequestType === 'feedback' || selectedRequestType === 'solution-story';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isFeedbackOrSolutionStory ? 'space-between' : 'flex-end',
        gap: 2,
        mt: 4,
        pt: 3,
        borderTop: `1px solid ${SEARCE_COLORS.border}`,
      }}
    >
      {isFeedbackOrSolutionStory ? (
        <>
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isFormDisabled}
            sx={{
              borderColor: SEARCE_COLORS.border,
              color: SEARCE_COLORS.textSecondary,
              textTransform: 'none',
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 500,
              '&:hover': {
                borderColor: SEARCE_COLORS.textSecondary,
                backgroundColor: alpha(SEARCE_COLORS.textSecondary, 0.04),
              },
              '&:disabled': {
                borderColor: alpha(SEARCE_COLORS.border, 0.5),
                color: alpha(SEARCE_COLORS.textSecondary, 0.5),
              },
            }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedRequestType === 'solution-story' && onSaveDraft && (
              <Button
                variant="outlined"
                type="button"
                onClick={onSaveDraft}
                disabled={isFormDisabled || draftSaving}
                sx={{
                  borderColor: SEARCE_COLORS.primary,
                  color: SEARCE_COLORS.primary,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: SEARCE_COLORS.primaryDark,
                    backgroundColor: alpha(SEARCE_COLORS.primary, 0.06),
                  },
                  '&:disabled': {
                    borderColor: alpha(SEARCE_COLORS.border, 0.5),
                    color: alpha(SEARCE_COLORS.textSecondary, 0.5),
                  },
                }}
              >
                {draftSaving ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <span>Saving...</span>
                  </Box>
                ) : (
                  'Save as draft'
                )}
              </Button>
            )}
            <Button
              variant="contained"
              type="submit"
              disabled={isFormDisabled}
              sx={{
                backgroundColor: SEARCE_COLORS.primary,
                color: SEARCE_COLORS.white,
                textTransform: 'none',
                px: 4,
                py: 1,
                borderRadius: 2,
                fontWeight: 500,
                boxShadow: 'none',
                minWidth: 100,
                '&:hover': {
                  backgroundColor: SEARCE_COLORS.primaryDark,
                  boxShadow: '0 2px 8px rgba(0, 100, 255, 0.3)',
                },
                '&:disabled': {
                  backgroundColor: alpha(SEARCE_COLORS.primary, 0.5),
                  color: SEARCE_COLORS.white,
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: 'white' }} />
                  <span>{selectedRequestType === 'solution-story' ? 'Submitting...' : 'Sending...'}</span>
                </Box>
              ) : submissionSuccess ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                  <span>{selectedRequestType === 'solution-story' ? 'Submitted!' : 'Sent!'}</span>
                </Box>
              ) : selectedRequestType === 'solution-story' ? 'Submit' : 'SEND'}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isFormDisabled}
            sx={{
              borderColor: SEARCE_COLORS.border,
              color: SEARCE_COLORS.textSecondary,
              textTransform: 'none',
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 500,
              '&:hover': {
                borderColor: SEARCE_COLORS.textSecondary,
                backgroundColor: alpha(SEARCE_COLORS.textSecondary, 0.04),
              },
              '&:disabled': {
                borderColor: alpha(SEARCE_COLORS.border, 0.5),
                color: alpha(SEARCE_COLORS.textSecondary, 0.5),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={isFormDisabled}
            sx={{
              backgroundColor: SEARCE_COLORS.primary,
              color: SEARCE_COLORS.white,
              textTransform: 'none',
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 500,
              boxShadow: 'none',
              minWidth: 120,
              '&:hover': {
                backgroundColor: SEARCE_COLORS.primaryDark,
                boxShadow: '0 2px 8px rgba(0, 100, 255, 0.3)',
              },
              '&:disabled': {
                backgroundColor: alpha(SEARCE_COLORS.primary, 0.5),
                color: SEARCE_COLORS.white,
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InlineSpinner size={18} thickness={4} />
                <span>Submitting...</span>
              </Box>
            ) : submissionSuccess ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                <span>Submitted!</span>
              </Box>
            ) : 'Submit'}
          </Button>
        </>
      )}
    </Box>
  );
};
